import { Box, IconIds, TextNew } from '@jfs/ui-new'
import { FC, ReactElement } from 'react'
import Tab from '../Tab'
import { TabState, TabType } from '../Tab/types'
import styles from './styles'

export interface PageSelectorProps {
  onSelect?: (currentPage: number, numRecords: number) => void
  title?: (currentPage: number, totalRecords: number, numRecords: number, totalPages: number) => string
  totalRecords: number
  default: number
  recordsPerPage: number
  type?: PageSelectorType
  hide?: boolean
  totalPages?: number
}

enum ArrowTypes {
  Left = 'Left',
  Right = 'Right',
}

export interface ArrowButtonProps {
  leftButtonPressed: () => void
  rightButtonPressed: () => void
  lastPageIndex: () => number
  pageIndex: number
  defaultPage: number
  totalRecords: number
  type: ArrowTypes
  hide: boolean
}

export enum PageSelectorType {
  default = 'default',
}

const titleString = (currentPage: number, totalRecords: number, numRecords: number, totalPages: number): string => {
  let records = Math.min(currentPage * numRecords, totalRecords)
  let one = records === 0 ? 0 : 1
  if (currentPage === totalPages) {
    return `${totalRecords - numRecords + one}-${records} of ${totalRecords}`
  }
  return `${currentPage * numRecords - numRecords + one}-${records} of ${totalRecords}`
}

const ArrowButton = ({
  leftButtonPressed,
  rightButtonPressed,
  lastPageIndex,
  pageIndex,
  defaultPage,
  totalRecords,
  type,
  hide,
}: ArrowButtonProps): ReactElement => {
  if (hide) {
    return <></>
  }
  let onSelection = rightButtonPressed
  let iconId = IconIds.ARROW_RIGHT
  if (pageIndex === 1 && type === ArrowTypes.Left) {
    onSelection = leftButtonPressed
    iconId = IconIds.ARROW_LEFT
  }

  const checkDisable =
    totalRecords === 0 ||
    (defaultPage === 1 && iconId === IconIds.ARROW_LEFT) ||
    (lastPageIndex() === defaultPage && iconId === IconIds.ARROW_RIGHT)

  const checkTabState = () => {
    if (defaultPage === pageIndex) {
      return TabState.selected
    }
    if (totalRecords === 0) {
      return TabState.selected
    }
    return TabState.normal
  }

  const tabState = checkTabState()

  return (
    <Tab iconId={iconId} isDisabled={checkDisable} state={tabState} type={TabType.arrow} onSelected={onSelection} />
  )
}

const PageSelector: FC<PageSelectorProps> = ({
  title = titleString,
  onSelect,
  default: defaultPage,
  totalRecords,
  recordsPerPage,
  hide = false,
  totalPages = -1,
}: PageSelectorProps) => {
  if (hide) {
    return null
  }

  const lastPageIndex = (): number => {
    if (totalPages === defaultPage) {
      return defaultPage
    }
    let lastPage = Math.floor(totalRecords / recordsPerPage)
    if (totalRecords % recordsPerPage > 0) {
      lastPage += 1
    }
    return lastPage
  }

  const leftButtonPressed = (): void => {
    if (defaultPage > 1) {
      const index = defaultPage - 1
      if (onSelect) {
        onSelect(index, recordsPerPage)
      }
    }
  }

  const rightButtonPressed = (): void => {
    if (defaultPage < lastPageIndex()) {
      const index = defaultPage + 1
      if (onSelect) {
        onSelect(index, recordsPerPage)
      }
    }
  }

  const isSinglePage = totalRecords <= recordsPerPage
  const containerStyle = isSinglePage ? styles.containerText : styles.container
  const separatorStyle = isSinglePage ? styles.seperatorText : styles.seperator

  return (
    <Box {...containerStyle}>
      <ArrowButton
        hide={isSinglePage}
        type={ArrowTypes.Left}
        pageIndex={1}
        leftButtonPressed={leftButtonPressed}
        rightButtonPressed={rightButtonPressed}
        lastPageIndex={lastPageIndex}
        defaultPage={defaultPage}
        totalRecords={totalRecords}
      />
      <Box {...separatorStyle} />
      <Box f={1} paddingHorizontal={8}>
        <TextNew {...styles.textStyle}>{title(defaultPage, totalRecords, recordsPerPage, totalPages)}</TextNew>
      </Box>
      <Box {...separatorStyle} />
      <ArrowButton
        hide={isSinglePage}
        type={ArrowTypes.Right}
        pageIndex={lastPageIndex()}
        leftButtonPressed={leftButtonPressed}
        rightButtonPressed={rightButtonPressed}
        lastPageIndex={lastPageIndex}
        defaultPage={defaultPage}
        totalRecords={totalRecords}
      />
    </Box>
  )
}

export { PageSelector }
